import { emotionTheme } from '@styles/theme'
import { Actions } from '@components/shared/Actions'
import { EditIcon } from '@assets/svgs/EditIcon'
import { DeleteIcon } from '@assets/svgs/DeleteIcon'
import { HeaderWithDate } from '@components/shared/HeaderWithDate'
import { numberMask } from '@utils/masks/cpfMask'
import { Tooltip } from '@components/shared/Tooltip'

import type { ColumnDefProps } from '@components/shared/Table/types'
import type { WeeklyBreakdownProps } from '../context/types'

interface ParseWeeklyBreakdownClientProps {
  data: WeeklyBreakdownProps[]
  weekSelected: number
  actionsDisabled?: boolean
  canEditServices?: boolean
  canDeleteServices?: boolean
  onEdit: (data: WeeklyBreakdownProps) => void
  onDelete: (data: WeeklyBreakdownProps) => void
}

export const parseWeeklyBreakdownClientGrid = ({
  data,
  weekSelected,
  onEdit,
  onDelete,
  actionsDisabled,
  canEditServices,
  canDeleteServices
}: ParseWeeklyBreakdownClientProps) => {
  if (!data) return []

  const weekendDays = ['domingo', 'sábado', 'sabado']
  const startIndex = (weekSelected - 1) * 7

  const referenceItem = data.reduce(
    (prev, curr) => ((curr.valorDias?.length || 0) < (prev.valorDias?.length || 0) ? curr : prev),
    data[0]
  )
  const referenceWeekDays = referenceItem?.valorDias?.slice(startIndex, startIndex + 7) || []

  const parsedData: ColumnDefProps[][] = data.map((item) => {
    const baseColumns: ColumnDefProps[] = [
      {
        id: 'grouping',
        columnName: 'Agrupamento',
        value: item.agrupamentoServico,
        minWidth: '10rem',
        sortable: true,
        headerAlign: 'center',
        bodyAlign: 'left'
      },
      {
        id: 'description',
        columnName: 'Descrição',
        value: `${item.cdItemVenda} - ${item.nmItemVenda}`,
        minWidth: '10rem',
        sortable: true,
        headerAlign: 'center',
        bodyAlign: 'left'
      },
      {
        id: 'grossUnitValue',
        columnName: 'Valor bruto unit.',
        value: item.vlBrutoUnitario,
        currency: true,
        align: 'center',
        minWidth: '11rem',
        sortable: true,
        headerAlign: 'center',
        bodyAlign: 'right'
      },
      {
        id: 'taxesPercent',
        columnName: 'Imposto (%)',
        value: item.contcVlrlFatPerimposto ? `${item.contcVlrlFatPerimposto.toFixed(2)}%`.replace('.', ',') : '-',
        align: 'center',
        maxWidth: '9rem',
        sortable: true
      },
      {
        id: 'totalQuantity',
        columnName: 'Qtd. total',
        value: item.qtFaturadaPrevia ? numberMask(String(item.qtFaturadaPrevia)) : '-',
        align: 'center',
        minWidth: '8rem',
        maxWidth: '8rem',
        sortable: true
      },
      {
        id: 'grossTotal',
        columnName: 'Total Bruto (R$)',
        value: item.vlTotalBrutoPrevia,
        currency: true,
        align: 'center',
        minWidth: '9rem',
        sortable: true,
        headerAlign: 'center',
        bodyAlign: 'right'
      },
      {
        id: 'netTotal',
        columnName: 'Total Líquido (R$)',
        value: item.vlTotalLiquidaPrevia,
        currency: true,
        align: 'center',
        minWidth: '9rem',
        sortable: true,
        headerAlign: 'center',
        bodyAlign: 'right'
      }
    ]

    const weekDaysColumns: ColumnDefProps[] =
      referenceWeekDays.length > 0
        ? referenceWeekDays.map((refDay, index) => {
            const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            const isWeekend = weekendDays.includes(refDay.dia?.toLowerCase() || '')
            const matchingDay = item.valorDias?.find((d) => d.data === refDay.data)

            return {
              id: `${dayNames[index]}${weekSelected}`,
              columnName: <HeaderWithDate item={refDay} />,
              value: matchingDay?.valor ? numberMask(String(matchingDay.valor)) : '-',
              align: 'center' as const,
              minWidth: '7rem',
              columnColor: isWeekend ? emotionTheme.colors.gray[100] : ''
            }
          })
        : []

    const actions = [
      ...(canEditServices ? [{ label: 'Editar', icon: <EditIcon />, onClick: () => onEdit(item) }] : []),
      ...(canDeleteServices ? [{ label: 'Excluir', icon: <DeleteIcon />, onClick: () => onDelete(item) }] : [])
    ]
    const isActionsDisabled = actionsDisabled || !actions.length

    const actionColumn: ColumnDefProps = {
      id: 'action',
      columnName: 'Ações',
      align: 'center',
      minWidth: '5rem',
      maxWidth: '5rem',
      stickyPosition: 'right',
      withoutTooltip: true,
      value: (
        <Tooltip title={isActionsDisabled ? 'Não é possível editar ou excluir serviço!' : ''} placement="top-end">
          <Actions actions={actions} disabled={isActionsDisabled} />
        </Tooltip>
      )
    }

    return [...baseColumns, ...weekDaysColumns, actionColumn]
  })

  return parsedData
}
